import { ReactNode, useState } from "react";
import Modal from "react-modal";

import BreadCrumb from "shared/BreadCrumb";
import { AppLayout } from "shared/Layout";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { getProductSectionData } from "shared/product/ProductSection/product-section.helper";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { withSeo } from "utils/with-seo.utils";

import { TipsList } from "../../[reference]/TipsList";
import TipsCloseIcon from "../../[reference]/tips-close.svg";

import { TipsMarkdown } from "./TipsMarkdown";
import TipsSummaryIcon from "./tips-summary.svg";

export type ProductTipPageProps = Exclude<
  Awaited<ReturnType<typeof getServerSideProps>>,
  { redirect: { destination: string } }
>["props"];

export const ProductTipPage = ({
  article,
  familyArticles,
  productSlug,
  productName,
  productBrand,
}: ProductTipPageProps) => {
  const i18n = useI18n();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <BreadCrumb
        className="!absolute"
        parts={[
          { label: i18n.t("breadcrumb.home"), to: "/" },
          { label: i18n.t("breadcrumb.catalog"), to: "/smartphone" },
          {
            label: i18n.t("breadcrumb.brand", {
              brand: productBrand === "Apple" ? "iPhone" : productBrand,
            }),
            to:
              productBrand === "Apple"
                ? "/smartphone/iphone"
                : "/smartphone/android",
          },
          {
            label: productName,
            to: `/smartphone/${productSlug}`,
          },
          { label: i18n.t("breadcrumb.tips"), to: "" },
        ]}
      />

      <section className="flex gap-5 px-5 my-16 mx-auto max-w-[1200px] md:my-24 lg:gap-16">
        <article className="flex-1">
          <button
            className="flex gap-2 items-center mb-5 text-lg font-medium text-cornflower-blue md:hidden"
            onClick={() => setIsOpen(true)}
          >
            <TipsSummaryIcon />
            {i18n.t("product.tipsSummary")}
          </button>
          <TipsMarkdown content={article.content} />
        </article>
        <aside className="hidden sticky top-10 py-10 px-8 w-80 h-fit bg-white rounded-md border border-black/10 md:block">
          <TipsList
            title={i18n.t("product.tipsSummary")}
            familyCategories={familyArticles}
            familySlug={productSlug}
          />
        </aside>
      </section>

      <Modal
        className="flex justify-center items-center w-screen h-screen"
        style={{
          content: { backgroundColor: "rgba(0, 0, 0, 0.4)" },
        }}
        overlayClassName={{
          base: "fixed inset-0 z-50",
          afterOpen: "",
          beforeClose: "",
        }}
        isOpen={isOpen}
      >
        <div className="relative p-8 m-5 w-full max-w-4xl bg-white rounded-xl">
          <button
            className="absolute top-3 right-3"
            onClick={() => setIsOpen(false)}
          >
            <TipsCloseIcon />
          </button>

          <TipsList
            title={i18n.t("product.tipsSummaryModal")}
            familyCategories={familyArticles}
            familySlug={productSlug}
          />
        </div>
      </Modal>
    </>
  );
};

ProductTipPage.getLayout = (page: ReactNode, pageProps: ProductTipPageProps) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

type GetServerSidePropsParams = {
  params: { slug: string; article: string };
};

export const getServerSideProps = async ({
  params: { article: tipsSlug, slug: productSlug },
}: GetServerSidePropsParams) => {
  const productData = await getProductSectionData(productSlug);
  const familyArticles = productData.family.tips || [];
  const article = familyArticles
    .flatMap((c) => c?.articles)
    .find((a) => a?.slug === tipsSlug);

  // variants is undefined, product probably doesn't exist
  if (!productData.variants) {
    return {
      redirect: {
        destination: "/smartphone",
      },
    };
  }

  // redirect if tips article not found
  if (!article) {
    return {
      redirect: {
        destination: `/smartphone/${productSlug}`,
      },
    };
  }

  const translations = await fetchTranslations([
    "marquee",
    "breadcrumb",
    "product",
  ]);

  return {
    props: {
      article,
      familyArticles,
      translations,
      seo: {
        ...article.seo,
        titleTemplate: "%s - MobileClub",
      },
      settings: await fetchSettings(),
      productSlug,
      productName: productData.family.modelName,
      productBrand: productData.family.brand,
    }
  };
};

export default withApp(ProductTipPage, [withSeo]);
